import { useEffect, useId, useState } from "react";
import { useBlocker, useSubmit } from "react-router";
import type { Entry, Team } from "@repo/shared-types";
import {
  LEVELS,
  draftAsEntryPart,
  levelLabel,
  draftSaysSomething,
  isScored,
  removalOf,
  type PartDraft,
  type RecordablePart,
} from "@/lib/results";
import { formatMoment, toWallTime } from "@/lib/times";
import {
  DivisionNote,
  Feel,
  PartRecorded,
  PendingBadge,
  ResultActions,
  ResultSummary,
  SharedBadge,
  partResult,
} from "@/components/result-fields";
import { TeamSummaries } from "@/components/team-fields";
import { PhotoField, PhotoView } from "@/components/photos";
import { PartDialog } from "@/components/part-dialog";
import { DiscardDialog, Modal } from "@/components/modal";
import { floatingGlass } from "@/components/navigation";
import { useTyping } from "@/hooks/use-typing";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

/**
 * A result recorded part by part, as an event's page and the log's page for
 * one result both do it (decided with Tiago 13 October). The page is read
 * first: no part offers a button until the bar at the bottom starts
 * recording — Record your workout, or Edit your result. Recording, each
 * part's sheet keeps what it read on the page, marked as not sent, and the
 * bar's one button sends it all with what covers the whole workout — its
 * one level on an event ranked overall, asked as recording starts, its
 * photo, and in the log when it was done — asking first, on a first result
 * that would be private, whether to share it. Cancel lets it all go;
 * leaving with anything not sent asks first.
 */

/** What a page's action says back about a workout sent, which decides what the page lets go of. */
export type WorkoutOutcome = { saved?: boolean; photoFailed?: boolean; photoAdded?: boolean };

/** How sending a private result was answered: whether to share it, and its notes, feel and photo with it. */
export type ShareAnswer = { share: boolean; detailsShared: boolean };

/**
 * What a page keeps until the workout is sent: whether it is recording, each
 * part's draft by its id, the sheet open, the questions it is asking, and
 * the whole workout's own fields — its one level, when it was done (as
 * typed, in the viewer's zone; null leaves it), a photo chosen or one to take
 * down.
 */
export function useWorkout({
  savedLevel,
  registered,
  single,
  outcome,
}: {
  /** The result's one level, when it has one. */
  savedLevel: string | null;
  /** Whether there is a result yet. */
  registered: boolean;
  /** One level for the whole event rather than one per part. */
  single: boolean;
  outcome: WorkoutOutcome | undefined;
}) {
  const [recording, setRecording] = useState(false);
  // The sheet the bar opens: what covers the whole workout.
  const [sheet, setSheet] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, PartDraft>>({});
  const [open, setOpen] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [askingLevel, setAskingLevel] = useState(false);
  // Cancel pressed with something typed: asked in a dialog before it goes.
  const [dropping, setDropping] = useState(false);
  const [level, setLevel] = useState(savedLevel ?? "rx");
  const [doneAt, setDoneAt] = useState<string | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  // A level saved since — once the workout is sent — replaces the one picked.
  // Adjusted while rendering, never in an effect: an effect also runs on
  // mount, and on a slow first render it could land after a click on the
  // level and undo it.
  const [readLevel, setReadLevel] = useState(savedLevel);
  if (savedLevel !== readLevel) {
    setReadLevel(savedLevel);
    setLevel(savedLevel ?? "rx");
  }
  // Sent: start again from what the page reads now, and go back to reading
  // it. A photo that did not go up stays, to be tried again.
  useEffect(() => {
    if (!outcome) return;
    setAsking(false);
    if (outcome.saved) {
      setDrafts({});
      setDoneAt(null);
      setRemovePhoto(false);
      setRecording(false);
      setSheet(false);
    }
    if ((outcome.saved && !outcome.photoFailed) || outcome.photoAdded) setPhoto(null);
    // A photo that did not go up waits in the sheet, to be tried again.
    if (outcome.photoFailed) setSheet(true);
  }, [outcome]);

  const pending = Object.keys(drafts).length;
  // A photo is something to send on its own, like a part.
  const changed =
    pending > 0 ||
    photo !== null ||
    removePhoto ||
    doneAt !== null ||
    (single && registered && level !== (savedLevel ?? "rx"));

  // Leaving the page loses what is not sent: asked first, by the bar's
  // `WorkoutQuestions`. Staying on it — a board's tab, a page of a thread —
  // is not asked about.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => changed && currentLocation.pathname !== nextLocation.pathname,
  );
  // Closing the tab or reloading: a browser shows only its own warning there.
  useEffect(() => {
    if (!changed) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [changed]);

  /** A part's draft, kept or let go of (undefined: nothing to send for it). */
  const setDraft = (partId: string, draft: PartDraft | undefined) =>
    setDrafts(({ [partId]: _, ...rest }) => (draft ? { ...rest, [partId]: draft } : rest));

  /**
   * Starts recording — asking first, in an event ranked overall with nothing
   * registered yet, its one level: chosen once, before any part.
   */
  const start = () => (single && !registered ? setAskingLevel(true) : setRecording(true));

  /** Stops recording, letting go of whatever was not sent — asked first, in a dialog, when there is any. */
  const cancel = () => (changed ? setDropping(true) : drop());

  /** Lets go of whatever was not sent, and goes back to reading the page. */
  const drop = () => {
    setDropping(false);
    setDrafts({});
    setDoneAt(null);
    setPhoto(null);
    setRemovePhoto(false);
    setLevel(savedLevel ?? "rx");
    setRecording(false);
    setSheet(false);
  };

  return {
    recording,
    setRecording,
    sheet,
    setSheet,
    start,
    cancel,
    drop,
    dropping,
    setDropping,
    blocker,
    drafts,
    setDraft,
    open,
    setOpen,
    asking,
    setAsking,
    askingLevel,
    setAskingLevel,
    level,
    setLevel,
    doneAt,
    setDoneAt,
    photo,
    setPhoto,
    removePhoto,
    setRemovePhoto,
    pending,
    changed,
  };
}

export type Workout = ReturnType<typeof useWorkout>;

/**
 * Sends the workout to the page's action: its body, built by the page in the
 * shape its route takes, and the photo chosen, or that the one saved goes.
 * `extra` names anything else the action reads ("first", "share").
 */
export function useSendWorkout(workout: Workout) {
  const submit = useSubmit();
  return (body: unknown, extra: Record<string, string> = {}) => {
    const data = new FormData();
    data.set("intent", "record");
    data.set("body", JSON.stringify(body));
    for (const [name, value] of Object.entries(extra)) data.set(name, value);
    if (workout.photo) data.set("file", workout.photo);
    else if (workout.removePhoto) data.set("removePhoto", "1");
    submit(data, { method: "post", encType: "multipart/form-data" });
  };
}

/** Whether a part has something saved its sheet can remove: a score, a note or a feel. */
export function hasSaved(part: RecordablePart): boolean {
  return !part.isTeam && Boolean(part.current?.recorded || part.current?.note || part.current?.feel);
}

/** What a part's sheet is called: what pressing its button does. */
function dialogTitle(part: RecordablePart, draft: PartDraft | undefined): string {
  const name = `${part.label}. ${part.title}`;
  if (part.isTeam) return `Start a team — ${name}`;
  if (!isScored(part)) return `Feel and note — ${name}`;
  return (draft && !draft.removed) || part.current?.recorded ? `Edit ${name}` : `Register ${name}`;
}

/**
 * The sheet of the part open: it starts from the part as it stands — the
 * draft typed before, or what is saved — and hands back what to keep for it:
 * a draft, a removal when it was emptied over something saved, or nothing.
 */
export function WorkoutPartDialog({
  part,
  index,
  workout,
  single,
  teamSlug,
}: {
  part: RecordablePart;
  index: number;
  workout: Workout;
  single: boolean;
  /** The space whose members a team's box suggests: a team is started on an event's page alone. */
  teamSlug?: string;
}) {
  const draft = workout.drafts[part.id];
  const keep = (next: PartDraft | undefined) => {
    workout.setDraft(part.id, next);
    workout.setOpen(null);
  };
  return (
    <PartDialog
      part={{ ...part, current: draft && !draft.removed ? draftAsEntryPart(part, draft) : part.current }}
      index={index}
      single={single}
      team={
        part.isTeam && teamSlug
          ? { slug: teamSlug, id: draft?.part.team?.id, people: draft?.part.team ?? undefined }
          : undefined
      }
      title={dialogTitle(part, draft)}
      removable={Boolean(draft) || hasSaved(part)}
      onSave={(read) => {
        const next = { ...read, removed: false };
        // Nothing typed: nothing to send, or, over a saved result, its removal.
        keep(draftSaysSomething(next) ? next : hasSaved(part) ? removalOf(part) : undefined);
      }}
      onRemove={() => keep(hasSaved(part) ? removalOf(part) : undefined)}
      onClose={() => workout.setOpen(null)}
    />
  );
}

/**
 * In an event ranked overall: Rx or Scaled, for every part. Asked as
 * recording starts when nothing is registered yet, and by "change" while
 * recording, the choice sent with the rest. A team the viewer is on holds
 * the level it is at (0037).
 */
export function LevelStep({
  eventTitle,
  level,
  registered,
  levelHeldBy,
  onChoose,
  onClose,
}: {
  eventTitle: string;
  level: string;
  registered: boolean;
  levelHeldBy: { part: string; level: string } | null;
  onChoose: (level: string) => void;
  onClose: () => void;
}) {
  return (
    <Modal title={`Rx or Scaled for ${eventTitle}?`} onClose={onClose}>
      <p className="text-sm text-muted-foreground">
        One level for every part: the event is ranked overall.{" "}
        {registered ? "Changing it changes it on every part." : "You can change it later."}
      </p>
      {levelHeldBy && (
        <p className="text-xs text-muted-foreground">
          You're on a team on {levelHeldBy.part} at {levelLabel(levelHeldBy.level)} — leave it to change your level.
        </p>
      )}
      <div className="grid grid-cols-2 gap-2">
        {/* Two buttons, not segments: choosing one is the answer, and closes the sheet. */}
        {LEVELS.map((option) => (
          <Button
            key={option.value}
            type="button"
            variant={registered && option.value === level ? "default" : "outline"}
            size="lg"
            disabled={levelHeldBy !== null && option.value !== levelHeldBy.level}
            onClick={() => onChoose(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </Modal>
  );
}

/**
 * Your result on one part, in its card: what is saved, or what was typed in
 * its sheet and is not sent yet — marked so — and, while recording, the
 * button that opens the sheet. On an event's page a part done in teams
 * lists your teams on it and, recording, starts another (`teams`); a part
 * not scored takes a feel and a note.
 */
export function PartResult({
  part,
  draft,
  registered,
  recording,
  showLevel,
  spaceName,
  teams,
  place = null,
  onOpen,
  onUndo,
}: {
  part: RecordablePart;
  draft: PartDraft | undefined;
  registered: boolean;
  /** Whether the page is recording: only then does a part offer its buttons. */
  recording: boolean;
  showLevel: boolean;
  spaceName: string | null;
  teams?: { teams: Team[]; base: string; eventId: string };
  /** Where the result stands on its board, on an event's page. */
  place?: { rank: number; of: number } | null;
  onOpen: () => void;
  onUndo: () => void;
}) {
  const notSent = registered ? "not saved yet" : "not registered yet";
  const undo = (
    <Button type="button" variant="outline" size="sm" onClick={onUndo}>
      Undo
    </Button>
  );

  if (part.isTeam && teams) {
    const typed = draft?.part.team ? draftAsEntryPart(part, draft) : null;
    const startable = !teams.teams.some((team) => team.role === "creator" || team.role === "member");
    if (teams.teams.length === 0 && !typed && !(recording && startable)) return null;
    return (
      <div className="space-y-2 border-t border-border pt-2">
        {teams.teams.length > 0 && <TeamSummaries teams={teams.teams} base={teams.base} eventId={teams.eventId} />}
        {typed && draft?.part.team ? (
          <div className="space-y-2 text-sm">
            <p>
              <span className="text-muted-foreground">Team to start </span>
              {/* The team's score as typed: read as a part's own, not as its teams'. */}
              <span className="font-medium">{partResult({ ...typed, isTeam: false })}</span>
              <span className="text-muted-foreground">
                {" "}
                with{" "}
                {[...draft.part.team.invite.map((name) => `@${name}`), ...draft.part.team.guests.map((guest) => guest.name)].join(", ")}
              </span>
              <PendingBadge>{notSent}</PendingBadge>
            </p>
            {recording && (
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={onOpen}>
                  Edit
                </Button>
                {undo}
              </div>
            )}
          </div>
        ) : (
          // One team a part (0036): none to start while you are on one.
          recording &&
          startable && (
            <Button type="button" variant="outline" size="sm" onClick={onOpen}>
              Start a team
            </Button>
          )
        )}
      </div>
    );
  }

  const shown = draft && !draft.removed ? draftAsEntryPart(part, draft) : part.current;
  const said = shown?.recorded || shown?.note || shown?.feel;
  if (!said && !recording) return null;
  const label = !isScored(part) ? "Add feel or note" : "Register";
  return (
    <div className="space-y-2 border-t border-border pt-2">
      {said &&
        (isScored(part) ? (
          <PartRecorded
            part={shown}
            showLevel={showLevel}
            spaceName={spaceName}
            pending={draft ? (draft.removed ? "will be removed" : notSent) : null}
            place={place}
          />
        ) : (
          <p className="text-sm">
            <Feel feel={shown?.feel ?? null} />
            {draft && <PendingBadge>{draft.removed ? "will be removed" : notSent}</PendingBadge>}
            {shown?.note && <span className="mt-0.5 block whitespace-pre-line text-xs text-muted-foreground">{shown.note}</span>}
          </p>
        ))}
      {recording && (
        <div className="flex gap-2">
          {/* Filled while nothing is said: until a part is, it is what recording is for. */}
          {!draft?.removed && (
            <Button type="button" size="sm" variant={said ? "outline" : "default"} onClick={onOpen}>
              {said ? "Edit" : label}
            </Button>
          )}
          {draft && undo}
        </div>
      )}
    </div>
  );
}

/**
 * Your workout, in a sheet the bar opens (decided with Tiago 13 October):
 * what covers all of it. Read, it says whether it is shared, its level and
 * when, shows its photo, and offers Share, Unshare and Delete. Recording, it
 * offers what goes with the parts: the event's one level, the photo, and —
 * in the log alone — when it was done.
 */
export function WorkoutSheet({
  entry,
  workout,
  zone,
  single,
  shareable,
  noDivision,
  profileDivision,
  deadlinePassed = false,
  spaceName,
  photoFailed,
  busy,
  images,
  logLink = false,
  doneAtEditable = false,
  onShare,
}: {
  entry: Entry | null;
  workout: Workout;
  zone: string;
  single: boolean;
  /** Whether it can be shared from here: a way back to its space, and a division. */
  shareable: boolean;
  /** Whether it could be on a board but has no division to be on one in. */
  noDivision: boolean;
  /** The division a result is saved in, said before the first one is. */
  profileDivision: string | null;
  /** Whether a part's deadline has passed: shared now, it would wait for verification. */
  deadlinePassed?: boolean;
  spaceName: string | null;
  /** The workout was sent and the photo chosen with it did not go up. */
  photoFailed: boolean;
  busy: boolean;
  /** Whether this deployment keeps pictures, and so offers a photo. */
  images: boolean;
  logLink?: boolean;
  /**
   * Whether when it was done can be changed: in the log alone. A result
   * recorded in a space carries when it was registered (decided with Tiago
   * 13 October); the log is where past workouts are set right.
   */
  doneAtEditable?: boolean;
  onShare: () => void;
}) {
  const recording = workout.recording;
  const doneId = useId();
  return (
    <Modal title="Your workout" onClose={() => workout.setSheet(false)}>
      {entry && <ResultSummary entry={entry} zone={zone} />}

      {recording && single && (
        <p className="text-sm text-foreground">
          Level, for the whole event: {levelLabel(workout.level)}{" "}
          <Button type="button" variant="link" onClick={() => workout.setAskingLevel(true)} className={inSentence}>
            change
          </Button>
          {entry && workout.level !== (entry.level ?? "rx") && <PendingBadge>not saved yet</PendingBadge>}
        </p>
      )}

      {recording &&
        doneAtEditable &&
        entry &&
        (workout.doneAt === null ? (
          <p className="text-sm text-foreground">
            Done {formatMoment(entry.performedAt, zone)}{" "}
            <Button
              type="button"
              variant="link"
              onClick={() => workout.setDoneAt(toWallTime(entry.performedAt, zone))}
              className={inSentence}
            >
              change
            </Button>
          </p>
        ) : (
          <Field className="max-w-xs gap-2">
            <FieldLabel htmlFor={doneId}>Done</FieldLabel>
            <Input
              id={doneId}
              type="datetime-local"
              value={workout.doneAt}
              onChange={(change) => workout.setDoneAt(change.target.value)}
            />
          </Field>
        ))}

      {images &&
        (photoFailed && workout.photo && entry ? (
          <RetryPhoto file={workout.photo} onDiscard={() => workout.setPhoto(null)} busy={busy} />
        ) : recording ? (
          <PhotoField
            photo={entry?.photo ?? null}
            file={workout.photo}
            onFile={workout.setPhoto}
            removing={workout.removePhoto}
            onRemoving={workout.setRemovePhoto}
            notSent={entry ? "not saved yet" : "not registered yet"}
            spaceName={spaceName}
            busy={busy}
          />
        ) : (
          entry?.photo?.status === "ready" && (
            <PhotoView photo={entry.photo} alt="Your photo" spaceName={spaceName} size="thumbnail" />
          )
        ))}

      {!entry && <DivisionNote profileDivision={profileDivision} />}
      {entry && !entry.shared && noDivision && <DivisionNote profileDivision={null} />}
      {entry && !entry.shared && deadlinePassed && (
        <p className="text-xs text-warning">
          A deadline has passed: shared now, a result on that part waits for the box to verify it before it shows.
        </p>
      )}

      {/* Unshare takes everything off, notes, feel and photo included;
          sharing again asks what goes with it (decided with Tiago 13 October). */}
      {entry && !recording && (
        <ResultActions
          entry={entry}
          canShare={shareable}
          busy={busy}
          onShare={() => {
            workout.setSheet(false);
            onShare();
          }}
          logLink={logLink}
        />
      )}
    </Modal>
  );
}

/**
 * The bar at the bottom, above the tab bar (decided with Tiago 13 October).
 * Read, it starts recording — Record your workout, or Edit your result once
 * there is one. Recording, it says what waits and sends it — Register
 * workout, then Save changes — or lets it go with Cancel. A spacer in the
 * page's flow keeps the end of the page clear of it.
 */
/**
 * What the bar asks before what was typed goes unsent: leaving the page, or
 * Cancel. The app's own dialogs, never the browser's `confirm` (decided with
 * Tiago 20 October).
 */
function WorkoutQuestions({ workout }: { workout: Workout }) {
  const { blocker } = workout;
  if (blocker.state === "blocked") {
    return (
      <DiscardDialog
        title="Leave without sending your workout?"
        confirm="Leave"
        onConfirm={() => blocker.proceed()}
        onClose={() => blocker.reset()}
      >
        What you typed is not saved yet.
      </DiscardDialog>
    );
  }
  if (workout.dropping) {
    return (
      <DiscardDialog title="Drop what you typed?" confirm="Drop it" onConfirm={workout.drop} onClose={() => workout.setDropping(false)}>
        None of it is sent yet.
      </DiscardDialog>
    );
  }
  return null;
}

export function RecordBar({
  workout,
  entry,
  busy,
  onSend,
}: {
  workout: Workout;
  /** The result, when there is one: its status, read, opens the sheet. */
  entry: Entry | null;
  busy: boolean;
  onSend: () => void;
}) {
  const registered = entry !== null;
  const typing = useTyping();
  // Toasts rise above the bar while it is there.
  useEffect(() => {
    document.documentElement.style.setProperty("--record-bar", "4.5rem");
    return () => {
      document.documentElement.style.removeProperty("--record-bar");
    };
  }, []);

  // What waits, said short: the bar is one line, and the sheet says the rest.
  const waiting = workout.pending;
  const said = [
    waiting === 1 ? "1 part" : waiting > 1 ? `${waiting} parts` : null,
    workout.photo || workout.removePhoto ? "photo" : null,
  ].filter(Boolean);
  const status = said.length > 0 ? capitalized(said.join(", ")) : workout.changed ? "Changes" : null;

  return (
    <>
      <WorkoutQuestions workout={workout} />
      <div aria-hidden="true" className="h-20" />
      {/* A card floating on the tab bar, at its width and in its glass (0082);
          not drawn while somebody types, as the tab bar is not (ADR 0071). */}
      <div hidden={typing} className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--tab-bar-top)+0.5rem)] z-20 px-4">
        <div
          role="region"
          aria-label="Recording"
          className={cn(floatingGlass, "pointer-events-auto p-2")}
        >
          {workout.recording ? (
            <div className="flex items-center gap-1.5">
              {/* Tight on a phone: what waits gives way first, then the buttons' padding. */}
              <Button
                type="button"
                variant="ghost"
                size="lg"
                onClick={() => workout.setSheet(true)}
                className="min-w-0 flex-1 shrink justify-start gap-0.5 px-3 font-normal text-muted-foreground"
              >
                <span className="truncate">
                  {status && <span className="text-warning">{status} · </span>}
                  <span className="font-medium text-foreground">Workout</span>
                </span>
                <ChevronRight aria-hidden="true" className="text-foreground" />
              </Button>
              <Button type="button" variant="outline" size="lg" onClick={workout.cancel} disabled={busy} className="px-3">
                Cancel
              </Button>
              <Button type="button" size="lg" onClick={onSend} disabled={busy || !workout.changed} className="px-3">
                {registered ? "Save changes" : "Register workout"}
              </Button>
            </div>
          ) : entry ? (
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="ghost"
                size="lg"
                aria-label={`Your workout: ${entry.shared ? "on the leaderboard" : "private"}`}
                onClick={() => workout.setSheet(true)}
                className="min-w-0 flex-1 shrink justify-start gap-1 px-3 text-muted-foreground"
              >
                <SharedBadge shared={entry.shared} />
                <ChevronRight aria-hidden="true" />
              </Button>
              <Button type="button" size="lg" onClick={workout.start} disabled={busy}>
                Edit your result
              </Button>
            </div>
          ) : (
            <Button type="button" size="lg" onClick={workout.start} disabled={busy} className="w-full">
              Record your workout
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

/** A button read as a word in its sentence: "Done 27 Sept 07:30 change". */
const inSentence = "h-auto p-0 align-baseline font-normal text-muted-foreground underline underline-offset-4";

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** A photo sent with the workout that did not go up: tried again, or let go. */
function RetryPhoto({ file, onDiscard, busy }: { file: File; onDiscard: () => void; busy: boolean }) {
  const submit = useSubmit();
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-warning">
      Your photo did not go up.
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() => {
          const data = new FormData();
          data.set("intent", "photo");
          data.set("file", file);
          submit(data, { method: "post", encType: "multipart/form-data" });
        }}
      >
        Try again
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={onDiscard}>
        Let it go
      </Button>
    </div>
  );
}
